import React, { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Password field with a single custom show/hide toggle.
 * Hides browser-native reveal controls so only this toggle is shown.
 * Forwards Input props; keeps data-testid on the input element.
 *
 * Optional `matchAgainst`: when set and the field has a value, shows a live
 * match/mismatch hint (for confirm-password fields).
 */
export function PasswordInput({ className, matchAgainst, id, ...props }) {
  const [visible, setVisible] = useState(false);
  const autoId = useId();
  const inputId = id || autoId;
  const value = props.value ?? '';
  const showMatch = matchAgainst !== undefined && String(value).length > 0;
  const matches = showMatch && String(value) === String(matchAgainst);
  const matchHintId = showMatch ? `${inputId}-match-hint` : undefined;

  return (
    <div>
      <div className="relative">
        <Input
          {...props}
          id={inputId}
          type={visible ? 'text' : 'password'}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-password-input=""
          aria-describedby={
            [props['aria-describedby'], matchHintId].filter(Boolean).join(' ') || undefined
          }
          className={cn('hireiq-password-input pr-10', className)}
        />
        <button
          type="button"
          className="absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          data-testid={
            props['data-testid']
              ? `${props['data-testid']}-visibility-toggle`
              : 'password-visibility-toggle'
          }
        >
          {visible ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
        </button>
      </div>
      {showMatch ? (
        <p
          id={matchHintId}
          className={cn(
            'mt-1 text-[11px]',
            matches ? 'text-emerald-700' : 'text-rose-700',
          )}
          data-testid={
            props['data-testid']
              ? `${props['data-testid']}-match-hint`
              : 'password-match-hint'
          }
          aria-live="polite"
        >
          {matches ? 'Passwords match' : 'Passwords do not match'}
        </p>
      ) : null}
    </div>
  );
}
